import type { FC } from "react";
import { useState } from "react";
import { tv } from "tailwind-variants";

import type { Action, ActionAction, HeaderSdk, Pages } from "~/core";
import type { GetSettingsQuery as GetSettingsQuerySdk } from "~/core/sdk";
import { Accordion, Flex, Image, useUiKitComponents } from "~/shared/ui-kit";
import { useDirectus } from "~/shared/utils/directus/useDirectus";

import { HeaderButton } from "./components/HeaderButton";
import { LocaleSelect } from "./components/LocaleSelect";
import { NavbarItem } from "./components/NavbarItem";
import { NavbarItemMobile } from "./components/NavbarItemMobile";

export type HeaderData = HeaderSdk & {
  language: string;
  availableLanguages: Array<string>;
  languages: GetSettingsQuerySdk["languages"];
};

export type HeaderProps = {
  data: HeaderData;
  clickHeaderMain: (targetUrl: string) => void;
  clickHeaderSub: (targetUrl: string) => void;
  onCallAction: (action: ActionAction) => void;
  isActionInactive: (control: Action) => boolean;
  getUrl: (page: Pages) => string;
};

export type MenuItemType = NonNullable<
  ExtractArrayItemType<
    NonNullable<NonNullable<NonNullable<HeaderSdk>["menu"]>["action_items"]>
  >
>["menu_action_items_id"];

const mobileNavigationWrapper = tv({
  base: "hidden lg:flex overflow-scroll lg:overflow-auto",
  variants: {
    open: {
      true: "flex flex-col fixed top-[52px] md:top-[100px] left-0 right-0 bottom-0 z-40 bg-[#000] pt-[25px] pb-[500px]",
    },
  },
});

const burgerButton = tv({
  base: "relative w-7 h-5 ml-5 lg:hidden cursor-pointer transition-all duration-300 ease-linear",
  variants: {
    open: {
      true: "rotate-45",
    },
  },
});

const burgerLine = tv({
  base: "absolute left-0 h-0.5 w-6 bg-[#fff] transition-all duration-300",
  variants: {
    index: {
      0: "top-0 delay-300",
      1: "top-[9px]",
      2: "top-[9px]",
      3: "top-[18px] delay-300",
    },
    open: {
      true: "",
      false: "",
    },
  },
  compoundVariants: [
    {
      index: 0,
      open: true,
      class: "opacity-0 transition-none",
    },
    {
      index: 2,
      open: true,
      class: "opacity-100 transform rotate-90",
    },
    {
      index: 3,
      open: true,
      class: "opacity-0 transition-none",
    },
  ],
});

export const HeaderV3: FC<HeaderProps> = ({
  data,
  clickHeaderMain,
  clickHeaderSub,
  onCallAction,
  isActionInactive,
  getUrl,
}) => {
  const { Link } = useUiKitComponents();
  const { getDirectusImage } = useDirectus();
  const [isBurgerMenuOpen, setBurgerMenuOpen] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<string>("");

  const onNavlinkClick = (url: string, id: string): void => {
    clickHeaderMain(url);
    setCurrentPage(id);
  };

  const onBurgerMenuClick = (): void => setBurgerMenuOpen(!isBurgerMenuOpen);

  const closeBurgerMenu = (): void => setBurgerMenuOpen(false);

  return (
    <header className="sticky top-0 bg-[#000] z-50 px-3 md:p-0">
      <Flex align="center" className="md:px-10 py-[9px] md:py-[25.5px]">
        {data?.logo && (
          <Link
            onClick={() => {
              closeBurgerMenu();
              onNavlinkClick("/", "");
            }}
            className="pr-0 md:pr-5 lg:pr-10"
            href="/"
          >
            <Image
              className="block max-w-[126px] md:max-w-[186px]"
              value={getDirectusImage(data.logo.id ?? "", {
                format: "svg",
                width: 126,
              })}
              mdValue={getDirectusImage(data.logo.id ?? "", {
                format: "svg",
                width: 186,
              })}
            />
          </Link>
        )}
        <Flex
          align="center"
          className={mobileNavigationWrapper({ open: isBurgerMenuOpen })}
        >
          {data?.menu?.action_items && (
            <Accordion>
              {data.menu.action_items
                ?.filter((action) => {
                  if (action?.menu_action_items_id?.action) {
                    return !isActionInactive(
                      action.menu_action_items_id.action,
                    );
                  }

                  return true;
                })
                ?.map(
                  (item) =>
                    item?.menu_action_items_id && (
                      <div
                        key={item?.menu_action_items_id.id}
                        className="w-full px-[15px] lg:w-auto lg:px-0"
                      >
                        <NavbarItem
                          {...item.menu_action_items_id}
                          currentNavlink={currentPage}
                          onNavlinkClick={onNavlinkClick}
                          clickHeaderSub={clickHeaderSub}
                          getUrl={getUrl}
                          onCallAction={onCallAction}
                        />
                        <NavbarItemMobile
                          {...item.menu_action_items_id}
                          closeBurgerMenu={closeBurgerMenu}
                          clickHeaderMain={clickHeaderMain}
                          clickHeaderSub={clickHeaderSub}
                          getUrl={getUrl}
                          onCallAction={onCallAction}
                        />
                      </div>
                    ),
                )}
            </Accordion>
          )}

          {data?.languages && (
            <div className="w-full px-[15px] lg:w-auto lg:px-0 block md:hidden">
              <LocaleSelect
                {...data}
                languages={data.languages}
                variant="static"
                offset={0}
              />
            </div>
          )}
          <div className="w-full px-[15px] lg:w-auto lg:px-0 block md:hidden">
            {data?.mobile_burger_menu && (
              <Flex align="center" flexDirection="column" gap={10}>
                {data.mobile_burger_menu.action_items
                  ?.filter((action) => {
                    if (action?.menu_action_items_id?.action) {
                      return !isActionInactive(
                        action.menu_action_items_id.action,
                      );
                    }

                    return true;
                  })
                  ?.map((item) => (
                    <HeaderButton
                      key={item?.id}
                      size="small"
                      weight="bold"
                      className="w-full px-3 md:px-6 py-[12px] my-4"
                      item={item?.menu_action_items_id}
                      getUrl={getUrl}
                      onCallAction={onCallAction}
                    />
                  ))}
              </Flex>
            )}
          </div>
        </Flex>
        <Flex className="flex-1 basis-0" />
        {data?.languages && (
          <Flex
            align="center"
            className="hidden px-2.5 md:block relative w-full md:w-auto mr-3"
          >
            <LocaleSelect
              {...data}
              languages={data.languages}
              variant="positioned"
              offset={30}
            />
          </Flex>
        )}
        {data?.additional_menu && (
          <Flex align="center" gap={10}>
            {data.additional_menu.action_items
              ?.filter((action) => {
                if (action?.menu_action_items_id?.action) {
                  return !isActionInactive(action.menu_action_items_id.action);
                }

                return true;
              })
              ?.map((item) => (
                <HeaderButton
                  key={item?.id}
                  size="xsmall"
                  md={{ size: "small" }}
                  weight="bold"
                  className="px-3 md:px-6 py-2 md:py-3"
                  item={item?.menu_action_items_id}
                  getUrl={getUrl}
                  onCallAction={onCallAction}
                />
              ))}
          </Flex>
        )}
        <div
          onClick={onBurgerMenuClick}
          className={burgerButton({ open: isBurgerMenuOpen })}
        >
          <span className={burgerLine({ index: 0, open: isBurgerMenuOpen })} />
          <span className={burgerLine({ index: 1, open: isBurgerMenuOpen })} />
          <span className={burgerLine({ index: 2, open: isBurgerMenuOpen })} />
          <span className={burgerLine({ index: 3, open: isBurgerMenuOpen })} />
        </div>
      </Flex>
    </header>
  );
};
